import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Grid,
  Header,
  Icon,
  Link,
  Popover,
  SpaceBetween,
} from '@amzn/awsui-components-react-v3';

import clsx from 'clsx';
import {isEmpty, uniqueId, upperCase} from 'lodash';
import {v4 as uuidv4} from 'uuid';

import {BoostMetaDataUnifiedReceiveRequest, updateBoostMetaDataUnifiedReceive} from '../../boost/utils';
import {RECEIVE_TYPES_UI} from '../../constants';
import {getSiteDeprecationStatus} from '../../constants/siteDeprecations';
import useLocalStorage from '../../hooks/useLocalStorage';
import {Context, Types} from '../../store';
import {AppContext, AppType} from '../../store/appContext';
import {
  Model,
  ReceiveExecutionType,
  ReceiveReferenceIdType,
  ReceiveType,
  ReceiveTypeNameMap,
  ReferenceQueryResponseItem,
  SearchReceiveReferencesRequest,
  Site,
} from '../../store/types';
import CopyText from '../CopyText/';
import ScanInput from '../ScanInput';
import SiteInput from '../SiteInput';
import {ValueWithLabel} from '../Utils';
import {isASNType} from '../Utils/Utils';
import {MultipleReferences} from './MultipleReferences';
import {ConfirmRMAID, IsRMAType, ShipmentMissingReasonModal} from './ShipmentMissingReason';
import {ErrorMessages} from './StartReceivingErrorMessages';
import {RMA_RECIEVE_PAYLOAD, SHIPMENT_MISSING_REASON_CODES} from './constants';
import useSearchReferences from './useSearchReferences';

export const StartReceiving = () => {
  const { state, dispatch } = useContext(Context);
  const shipmentIdQuery = state?.initialShipmentReferenceId;
  const shouldRefetchReference = state?.shouldRefetchReference;
  const { referenceId, receiveType } = useParams();
  const isAdjustment = receiveType === ReceiveType.INVENTORY_ADJUSTMENT;
  const [showReasonCodeSelection, setShowReasonCodeSelection] = useState(false);
  const [editTrackingID, setEditTrackingID] = useState(false);
  const [confirmRma, setConfirmRMA] = useState(false);
  const [clearSiteName, setClearSiteName] = useState(false);
  const [preferredSite] = useLocalStorage('unifiedReceivePreferredSite');

  const appContext = useContext(AppContext);
  const mountRef = useRef<Boolean>(false);

  const referenceData = state?.receiveReference?.data;
  const references: ReferenceQueryResponseItem[] = state?.receiveReferences || [];
  const reference: ReferenceQueryResponseItem | undefined = references.find(
    (ref: ReferenceQueryResponseItem) => ref.receiveReference?.receiveReferenceId === referenceId,
  );
  const receiveReference = reference?.receiveReference;
  const preferredSiteName = clearSiteName ? null : preferredSite?.value;
  const siteName = !isEmpty(receiveReference?.siteName) ? receiveReference?.siteName : preferredSiteName;
  const [editSiteName, setEditSiteName] = useState(isEmpty(siteName));
  const isExistingDraftRma = receiveReference?.externalReferenceType !== ReceiveType.ASN_RMA;
  const [rma, setRma] = React.useState(receiveType === 'no_reference' && !isExistingDraftRma);
  const history = useHistory();
  const urlParams = new URLSearchParams(window.location.search);
  const shipmentId = urlParams.get('shipmentId');
  const [inputVal, setInputVal] = useState(shipmentId || receiveReference?.externalReferenceValue || shipmentIdQuery);
  const [resetKey, setResetKey] = useState(uniqueId());
  const isRMA = rma;

  const { searchReferences, loading, request, multipleReferencesFound, setMultipleReferencedFound } =
    useSearchReferences({ rma });

  const multipleReferences = multipleReferencesFound?.receiveReferenceQueryResponseList || [];
  const multipleReferencesPO = multipleReferences.some(
    (val: ReferenceQueryResponseItem) => val?.receiveReference?.externalReferenceType === ReceiveType.AWS_PO,
  );
  const multipleReferencesASN = multipleReferences.some((val: ReferenceQueryResponseItem) =>
    isASNType(val?.receiveReference?.externalReferenceType),
  );

  const multipleReferencesVisibleColumns = useMemo(() => {
    if (multipleReferencesPO && multipleReferencesASN) {
      return ['carrier', 'supplier', 'trackingId', 'sitename', 'externalReferenceType'];
    }

    if (multipleReferencesPO) {
      return ['supplier', 'sitename'];
    }

    return ['carrier', 'supplier', 'trackingId', 'sitename'];
  }, [multipleReferencesPO, multipleReferencesASN]);

  const isPO = reference?.scannableIdFoundAs === ReceiveReferenceIdType.ORDER_NUMBER;
  const siteNameLabel = isRMA || isAdjustment ? 'Receiving Site' : 'Ship To site';

  const receiveTitle = isPO ? 'PO' : receiveType;

  const shipmentMissingReason = reference
    ? SHIPMENT_MISSING_REASON_CODES.find((r) => r.value === reference.shipmentMissingReasonCode)
    : null;

  const showTrackingInput = isEmpty(receiveReference?.trackingId) || editTrackingID;
  const draft = state?.models?.find(
    (model: Model) =>
      model.referenceId === reference?.receiveReference?.receiveReferenceId && !model.receiveExecutionUUId,
  );

  const statusCode = referenceData?.status?.statusCode;
  const error = state?.receiveReference?.errors?.[0] || statusCode;
  const showReferenceData = reference && !loading;

  const scanReference = (value: string, type: ReceiveReferenceIdType) => {
    const request: SearchReceiveReferencesRequest = {
      referenceIdList: [{ type, value }],
      locationIdList: [],
    };
    dispatch({
      type: Types.scanReference,
      payload: {
        errors: null,
        loading: true,
        data: [],
      },
    });
    searchReferences({ variables: request });
  };

  const updateBoostMetaDataFn = useCallback(
    (trackingNumber?: string, siteName?: string) => {
      if (appContext.appType === AppType.ARGO) {
        return;
      }

      if (!siteName) {
        siteName = reference?.receiveReference.siteName || reference?.siteName;
      }
      let receiveType = !rma ? ReceiveExecutionType.PRECONDITION_RECEIVE : ReceiveExecutionType.RMA;

      if (siteName && reference?.receiveReference.externalReferenceType) {
        const metaData: BoostMetaDataUnifiedReceiveRequest = {
          referenceId,
          referenceType: reference?.receiveReference.externalReferenceType,
          receivingSite: siteName,
          trackingNumber,
          vendor: reference?.receiveReference.supplier,
          receiveType
        };
        updateBoostMetaDataUnifiedReceive(state?.workRequestId, metaData);
      }
    },
    [state?.workRequestId, reference?.siteName, reference?.receiveReference, appContext.appType, referenceId, rma],
  );

  useEffect(() => {
    // on mount update boost initial meta data
    if (!loading && reference && !mountRef.current) {
      mountRef.current = true;
      updateBoostMetaDataFn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateBoostMetaDataFn, reference, loading]);

  useEffect(() => {
    shouldRefetchReference && scanReference(shipmentIdQuery, ReceiveReferenceIdType.NON_SPECIFIC);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefetchReference]);

  useEffect(() => {
    if (!reference?.receiveReference?.siteName) {
      dispatch({
        type: Types.updateSiteName,
        payload: {
          siteName: preferredSite?.value,
          siteId: preferredSite?.locationId,
          referenceId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConfirmMultipleReferences = (reference: ReferenceQueryResponseItem) => {
    dispatch({
      type: Types.scanReference,
      payload: {
        errors: [],
        loading: false,
        data: { ...multipleReferencesFound, receiveReferenceQueryResponseList: [reference] },
      },
    });
    setMultipleReferencedFound(null);
    const receiveType = RECEIVE_TYPES_UI[reference.scannableIdFoundAs];
    history.push(`/receive/${receiveType}/${reference?.receiveReference?.receiveReferenceId}?${urlParams.toString()}`);
  };

  const onDismissMultipleReferences = () => {
    setMultipleReferencedFound(null);
  };

  const cancelReference = () => {
    dispatch({
      type: Types.clearReference,
      payload: {
        referenceId: '',
      },
    });
    setRma(false);
  };

  const onHandleConfirmRmaType = () => {
    setConfirmRMA(true);
  };

  const onHandleConfirmRma = () => {
    const rmaUUID = uuidv4();
    const rmaPayload = {
      ...RMA_RECIEVE_PAYLOAD,
      scanStartTime: Date.now(),
      receiveReference: {
        ...RMA_RECIEVE_PAYLOAD.receiveReference,
        expectedReceiveLocation: preferredSite?.locationId,
        receiveReferenceId: rmaUUID,
        externalReferenceValue: inputVal,
        siteName: preferredSite?.value,
      },
    };
    setConfirmRMA(false);
    dispatch({
      type: Types.scanReference,
      payload: {
        data: {
          receiveReferenceQueryResponseList: [rmaPayload],
        },
        loading,
        errors: [],
      },
    });
    history.push(`/receive/no_reference/${rmaUUID}?${urlParams.toString()}`);
  };

  const onHandleConfirmClearReference = () => {
    setInputVal('');
    cancelReference();
    history.push(`/receive/`);
  };

  const handleCancelPO = () => {
    dispatch({
      type: Types.scanReference,
      payload: {
        data: [],
        loading,
        errors: ['shipment_not_found'],
      },
    });
  };

  const onDismissRMA = () => {
    dispatch({
      type: Types.scanReference,
      payload: {
        data: [],
        loading,
        errors: ['po_not_found'],
      },
    });
  };

  const resetReceive = () => {
    dispatch({
      type: Types.scanReference,
      payload: {
        errors: null,
        loading: false,
        data: [],
      },
    });
    setInputVal('');
    setConfirmRMA(false);
    history.push(`/receive/`);
    setRma(false);
    setShowReasonCodeSelection(false);
  };

  const handleOnScanReference = (value: string, type: ReceiveReferenceIdType) => {
    if (value && value.length > 0) {
      scanReference(value, type);
    }
  };

  const handleOnScanTrackingId = (value: string) => {
    if (value && value.length > 0) {
      dispatch({
        type: Types.scanTrackingId,
        payload: {
          trackingId: value,
          referenceId,
        },
      });
      setEditTrackingID(false);
      updateBoostMetaDataFn(value);
    }
  };

  const handleOnUpdateSiteName = (siteOption: Site) => {
    setEditSiteName(false);
    setClearSiteName(false);
    dispatch({
      type: Types.updateSiteName,
      payload: {
        siteName: siteOption.value,
        siteId: siteOption.locationId,
        referenceId,
      },
    });
    updateBoostMetaDataFn(reference?.receiveReference.trackingId, siteOption.value);
  };

  const cancelResetPopover = () => setResetKey(uniqueId());

  const getLabel = () => {
    if (isPO) {
      return 'Order Number';
    }
    if (isRMA) {
      return 'RMA ID';
    }
    return 'Shipment ID';
  };

  const isShipmentPredetermined = !!shipmentIdQuery;
  const isNewShipmentAllowed = (reference || error) && draft && !isShipmentPredetermined;

  // Block actions when the effective receive site (siteName, which may come from
  // the loaded reference or from preferredSite) is past its deprecation cutoff.
  // Without this gate, a user with a non-deprecated preferredSite can still start
  // a receive against e.g. IAD057 / IAD058 by editing the site here, even though
  // those sites have migrated to Boost. See src/constants/siteDeprecations.ts.
  const deprecation = getSiteDeprecationStatus(siteName);
  const isSiteBlocked = deprecation.status === 'blocked';

  return (
    <Container
      header={
        <Header
          variant="h2"
          description={
            showReferenceData ? null : (
              <Box color="text-status-inactive" fontSize="body-s" variant="span">
                <ErrorMessages code="shipment_po_rma_not_available_to_scan" />
              </Box>
            )
          }
          actions={
            <div className={clsx('shipment-receive-action', { error })}>
              {isNewShipmentAllowed && (
                <Popover
                  key={resetKey}
                  dismissButton={true}
                  position="top"
                  size="large"
                  triggerType="custom"
                  header="Confirm start new shipment"
                  content={
                    <Box>
                      <p>
                        Are you sure you want to start new shipment? You will lose data, and this action is
                        irreversible.
                      </p>
                      <div className="confirm-actions">
                        <SpaceBetween direction="horizontal" size="xs">
                          <Button variant="link" onClick={cancelResetPopover}>
                            Cancel
                          </Button>
                          <Button
                            variant="link"
                            ariaLabel="Confirm start new shipment"
                            onClick={onHandleConfirmClearReference}
                          >
                            <SpaceBetween direction="horizontal" size="xs">
                              <Icon name="remove" variant="error" />
                              <Box variant="span" color="text-status-error">
                                Clear
                              </Box>
                            </SpaceBetween>
                          </Button>
                        </SpaceBetween>
                      </div>
                    </Box>
                  }
                >
                  {reference && (
                    <Button iconName="close" ariaLabel="Start new shipment">
                      Start new shipment
                    </Button>
                  )}
                </Popover>
              )}
              {(reference || error) && !draft && !shipmentIdQuery && (
                <Button iconName="close" ariaLabel="Start new shipment" onClick={onHandleConfirmClearReference}>
                  Start new shipment
                </Button>
              )}
            </div>
          }
        >
          Start Receiving {upperCase(rma ? 'rma' : receiveTitle).replace('_', ' ')}
        </Header>
      }
    >
      {!!multipleReferencesFound && (
        <MultipleReferences
          references={multipleReferencesFound?.receiveReferenceQueryResponseList}
          onConfirm={onConfirmMultipleReferences}
          onDismiss={onDismissMultipleReferences}
          referenceValue={request?.referenceIdList?.[0]?.value || inputVal}
          visibleColumns={multipleReferencesVisibleColumns}
        />
      )}
      <SpaceBetween size="xxl">
        {isSiteBlocked && (
          <Alert
            statusIconAriaLabel="Error"
            type="error"
            header="This site is no longer available"
          >
            {deprecation.message}
            {deprecation.boostLink && (
              <>
                {' '}
                <Link href={deprecation.boostLink} external>
                  Go to Boost Receive.
                </Link>
              </>
            )}
          </Alert>
        )}
        {!isSiteBlocked && (
        <Box margin={{ top: 'xl' }}>
          <ColumnLayout columns={reference && !loading ? 3 : 1} variant="text-grid">
            {!!reference ? (
              <ValueWithLabel label={<Box>{getLabel()}</Box>}>
                <CopyText label={getLabel()} value={receiveReference?.externalReferenceValue || ''}>
                  {receiveReference?.externalReferenceValue}
                </CopyText>
                {reference.shipmentMissingReasonCode && shipmentMissingReason && (
                  <Box margin={{ right: 's', top: 's' }}>
                    <Link variant="secondary" href="#" onFollow={() => setShowReasonCodeSelection(true)}>
                      {shipmentMissingReason.label}
                    </Link>
                  </Box>
                )}
              </ValueWithLabel>
            ) : (
              <ScanInput
                label={isRMA ? 'Scan rma ID' : 'Scan shipment ID'}
                inputValue={inputVal}
                onScan={(value: string) => handleOnScanReference(value, ReceiveReferenceIdType.NON_SPECIFIC)}
                placeholder={isRMA ? 'Scan rma ID' : 'Scan shipment ID or PO if shipment ID is not available'}
                onChange={(val) => setInputVal(val)}
                autoFocus
                controlId="scan-shipmentid-input"
                readOnly={!!reference || loading}
                disabled={receiveType === 'po'}
                clearOnSubmit={false}
                loading={loading}
                ariaLabel={
                  receiveReference?.externalReferenceValue
                    ? `scan-input-${receiveReference?.externalReferenceValue}`
                    : 'scan-input'
                }
              />
            )}

            {showReferenceData && (
              <>
                {!isPO && receiveReference?.carrier && (
                  <ValueWithLabel label="Carrier">{receiveReference?.carrier}</ValueWithLabel>
                )}
                {receiveReference?.supplier && (
                  <ValueWithLabel label="Vendor">{receiveReference?.supplier}</ValueWithLabel>
                )}
                {receiveReference?.externalReferenceType && (
                  <ValueWithLabel label="Receive Type">
                    {ReceiveTypeNameMap[receiveReference?.externalReferenceType]}
                  </ValueWithLabel>
                )}
                {showTrackingInput ? (
                  <ValueWithLabel label="Tracking ID">
                    <div className="tracking-id-editable">
                      <ScanInput
                        inputValue={receiveReference?.trackingId}
                        onScan={(value: string) => handleOnScanTrackingId(value)}
                        placeholder="Scan tracking ID"
                        validationErrorText="Tracking ID should not contain space or certain special characters"
                        clearOnSubmit={false}
                        ariaLabel="scan tracking id"
                        disableFocus
                      />
                      {editTrackingID && (
                        <Button
                          href="#"
                          iconName="close"
                          variant="inline-link"
                          iconAlign="right"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setEditTrackingID(false);
                          }}
                        />
                      )}
                    </div>
                  </ValueWithLabel>
                ) : (
                  <ValueWithLabel label="Tracking ID">
                    {receiveReference?.trackingId && (
                      <>
                        <CopyText label="Tracking ID" value={receiveReference?.trackingId}>
                          {receiveReference?.trackingId}
                        </CopyText>
                        <Button
                          href="#"
                          iconName="edit"
                          variant="inline-link"
                          ariaLabel="edit tracking id"
                          iconAlign="right"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setEditTrackingID(true);
                          }}
                        />
                      </>
                    )}
                  </ValueWithLabel>
                )}
                {editSiteName || isEmpty(siteName) ? (
                  <ValueWithLabel label={siteNameLabel} className="site-name">
                    <Grid gridDefinition={[{ colspan: 10 }]}>
                      <SiteInput siteName={siteName} isPreferredSite={false} onSiteChange={handleOnUpdateSiteName} />
                    </Grid>
                  </ValueWithLabel>
                ) : (
                  <ValueWithLabel label={siteNameLabel}>
                    <CopyText label={siteNameLabel} value={siteName || ''}>
                      {siteName}
                    </CopyText>
                    <Button
                      href="#"
                      iconName="edit"
                      variant="inline-link"
                      ariaLabel="edit site name"
                      iconAlign="right"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setEditSiteName(true);
                      }}
                    />
                  </ValueWithLabel>
                )}

                {false && receiveReference?.expectedDeliveryDateTime && (
                  <ValueWithLabel label="ETA">
                    {receiveReference?.expectedDeliveryDateTime &&
                      `${new Date(parseFloat(receiveReference?.expectedDeliveryDateTime)).toLocaleDateString('en-US')} -
                  ${
                    receiveReference?.expectedDeliveryDateTime &&
                    new Date(parseFloat(receiveReference?.expectedDeliveryDateTime)).toLocaleTimeString('en-US')
                  }`}
                  </ValueWithLabel>
                )}
              </>
            )}
          </ColumnLayout>
          <Box color="text-status-error" margin={{ top: 's' }}>
            {error && error !== 'shipment-type-po' && error !== 'shipment_id_not_found' && (
              <Alert statusIconAriaLabel="Error" type="error" dismissible onDismiss={resetReceive}>
                <ErrorMessages code={error} inputReference={inputVal} />
              </Alert>
            )}
            {referenceId && isRMA && (
              <Box margin={{ top: 'l' }}>
                <Alert statusIconAriaLabel="Info" type="info">
                  This receive type is for Inbound RMA replacement from the vendor.
                </Alert>
              </Box>
            )}
            {!reference?.receiveReference && !loading && confirmRma && (
              <ConfirmRMAID
                setRma={setRma}
                rma={request?.referenceIdList?.[0]?.value || inputVal}
                onConfirm={onHandleConfirmRma}
                onDismiss={onDismissRMA}
              />
            )}
            {!confirmRma && error === 'shipment_id_not_found' && (
              <IsRMAType
                rma={request?.referenceIdList?.[0]?.value}
                onConfirm={onHandleConfirmRmaType}
                onDismiss={onDismissRMA}
              />
            )}
            {((error === 'shipment-type-po' && !reference) || showReasonCodeSelection) && (
              <ShipmentMissingReasonModal
                onDismiss={() => {
                  reference ? setShowReasonCodeSelection(false) : handleCancelPO();
                }}
                selected={shipmentMissingReason}
                onConfirm={(shipmentMissingReasonCode: string) => {
                  dispatch({
                    type: Types.scanReference,
                    payload: {
                      data: { ...referenceData, shipmentMissingReasonCode },
                      loading,
                      errors: [],
                    },
                  });
                  setShowReasonCodeSelection(false);
                  history.push(
                    `/receive/po/${referenceData?.receiveReferenceQueryResponseList?.[0]?.receiveReference?.receiveReferenceId}`,
                  );
                }}
              />
            )}
          </Box>
        </Box>
        )}
      </SpaceBetween>
    </Container>
  );
};
